<script setup>
import {tvCompactTeamName, tvPages, tvPageSize, tvWhen} from '~/utils/tvDisplay';

// Ladder challenges waiting for a reply, or accepted and waiting to be played.
// The players lead: challenger over challenged, with the ladder's outgoing and
// incoming arrows. When it's on sits on the label line above, like a result's date.
//   tester                      Wed 18:25
//   ┌──────────────────────────────────┐
//   │ ↗ 11 a                      61%  │
//   │ ↙ 4 a                            │
//   └──────────────────────────────────┘
const props = defineProps({
  panel: {type: Object, required: true},
  page: {type: Number, default: 0},
});
// wide, column or portrait (set by the panel's frame)
const mode = inject('tvMode', ref('wide'));

const rows = computed(() => tvPages(props.panel.challenges, tvPageSize('challenges', mode.value))[props.page] ?? []);
</script>

<template>
  <div class="tv-chs">
    <div v-for="(c, i) in rows" :key="i" class="tv-chc">
      <div class="tv-chc-meta">
        <v-icon icon="mdi-ladder" size="18"/>
        <span class="tv-chc-ladder">{{ c.ladderName }}</span>
        <span class="tv-chc-when" :class="{'tv-chc-when--set': c.accepted && c.when}">
          <template v-if="c.accepted && c.when">{{ tvWhen(c.when) }}</template>
          <template v-else-if="c.accepted">Time to agree</template>
          <template v-else>Awaiting reply</template>
        </span>
      </div>
      <div class="tv-chc-card">
          <div class="tv-chc-side">
            <v-icon icon="mdi-arrow-top-right" size="20" class="tv-chc-out"/>
            <span class="tv-chc-name">{{ tvCompactTeamName(c.challengerName) }}</span>
            <span v-if="c.chanceOfSuccess != null" class="tv-chc-odds">{{ c.chanceOfSuccess }}%</span>
          </div>
          <div class="tv-chc-side">
            <v-icon icon="mdi-arrow-bottom-left" size="20" class="tv-chc-in"/>
            <span class="tv-chc-name">{{ tvCompactTeamName(c.challengedName) }}</span>
          </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tv-chs {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.tv-chc-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 18px;
  color: var(--tv-muted);
}

.tv-chc-ladder {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The same card as a scorebug's names: two rows */
.tv-chc-card {
  border-radius: 6px;
  overflow: hidden;
  background: var(--tv-panel);
  border: 1px solid var(--tv-line);
}

.tv-chc-side {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 14px 0 10px;
  font-size: 22px;
  color: var(--tv-ink);

  & + & {
    border-top: 1px solid var(--tv-line);
  }
}

.tv-chc-out {
  color: var(--tv-accent-text);
}

.tv-chc-in {
  color: #1e9e76;
}

.tv-chc-name {
  min-width: 0;
  letter-spacing: -0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The AI's chance the challenger wins, quiet beside their name */
.tv-chc-odds {
  margin-left: auto;
  padding-left: 12px;
  font-family: var(--tv-display);
  font-weight: 600;
  font-size: 20px;
  color: var(--tv-muted);
}

/* When it's on: supporting detail, at the end of the label line */
.tv-chc-when {
  margin-left: auto;
  padding-left: 16px;
  white-space: nowrap;
}

.tv-chc-when--set {
  font-weight: 600;
  color: var(--tv-ink);
}
</style>
